import React from "react";

interface ResultsHeaderProps {
    currentStep?: number;
}

const ResultsHeader: React.FC<ResultsHeaderProps> = ({ currentStep = 1 }) => {
    const steps = [
        { number: 1, label: "Select flights" },
        { number: 2, label: "Travelers" },
        { number: 3, label: "Seat" },
        { number: 4, label: "Extras" },
        { number: 5, label: "Payment" },
        { number: 6, label: "Confirmation" },
    ];

    return (
        <header className="bg-white shadow-sm border-b border-gray-200 h-[72px] flex items-center">
            <div className="max-w-[1120px] mx-auto px-6 w-full">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-10">
                        {/* SAS Logo */}
                        <svg
                            viewBox="0 0 40 16"
                            className="h-6"
                            style={{ minWidth: "80px" }}
                        >
                            <path
                                fill="#00175c"
                                d="M12.3226 0C14.914 0 16.2091 0.68341 16.6805 0.91080 17.0350 1.08211 16.9759 1.30950 16.9759 1.30950L16.6675 2.36635C16.6471 2.44958 16.6317 2.53395 16.6213 2.61901C16.5628 2.73302 16.4451 2.73302 16.3268 2.61901L15.7956 2.31336C15.1455 1.96148 14.3771 1.65152 13.3240 1.65152C11.7342 1.65152 10.9090 2.79032 11.3212 4.44124L11.6072 5.49068C11.9363 6.74676 12.2556 8.00536 12.5654 9.26636L12.6763 9.73656C13.3825 12.5836 11.3804 16 5.9626 16C2.9592 16 1.0748 14.9179 0.4266 14.4058C0.0735 14.1211 -0.0442 13.9498 0.0143 13.7791L0.1918 13.2146C0.3113 12.8461 0.4531 12.4382 0.4857 12.4696C0.4857 12.2416 0.6034 12.1849 0.7211 12.2989L0.9921 12.5528C1.7027 13.1936 3.2063 14.3487 5.1967 14.3487C7.7293 14.3487 7.5525 12.3556 7.5525 12.0709C7.5525 11.2742 6.4926 7.3449 6.1980 6.3201C5.3736 3.01772 7.4348 0 12.3226 0ZM41.0020 0C43.5932 0 44.8884 0.68341 45.3598 0.91080C45.7143 1.08211 45.6552 1.30950 45.6552 1.30950L45.3476 2.36635C45.3269 2.44958 45.3114 2.53395 45.3011 2.61901C45.2426 2.73302 45.1249 2.73302 45.0071 2.61901C44.2412 2.16362 43.3584 1.65152 42.0041 1.65152C40.4141 1.65152 39.5889 2.79032 40.0011 4.44124L40.1937 5.13388C40.5452 6.25697 41.2146 9.09875 41.3557 9.73716C42.0625 12.5842 40.0604 16 34.6425 16C31.6391 16 30.0724 14.9752 29.4248 14.4625C29.1302 14.2252 28.9996 14.0675 28.9977 13.9220L29.2763 12.5682C29.2855 12.5281 29.2935 12.4979 29.2984 12.4800C29.3662 12.2989 29.5425 12.2422 29.6602 12.3556L30.1248 12.7598C30.6920 13.4007 32.0396 14.3487 33.8766 14.3487C36.4093 14.3487 36.2324 12.3556 36.2324 12.0709C36.2324 11.2742 35.1725 7.3449 34.8779 6.3201C34.0528 3.01772 36.1140 0 41.0020 0ZM29.8718 0.17070C30.0135 0.17070 30.0037 0.28039 30.0246 0.32414L30.0486 0.34140L26.9861 15.6013C26.9861 15.6870 26.9528 15.7079 26.9121 15.7134L21.3914 15.7153C20.8152 15.7153 20.8152 15.6013 20.8152 15.5446L21.4542 12.2940C21.4578 12.2768 21.4606 12.2596 21.4628 12.2422L21.4474 12.2021L21.4043 12.1849H17.2690L17.2129 12.1997L17.1513 12.2422L14.4424 15.6013C14.4424 15.6444 14.4097 15.6870 14.3185 15.7060L14.2076 15.7153H12.3220C12.2345 15.7153 12.2123 15.6832 12.2314 15.6192L12.2634 15.5446L24.9248 0.28470L24.9815 0.24465C25.0342 0.21480 25.0914 0.19397 25.1510 0.18302L25.2585 0.17070H29.8718ZM23.2758 4.66924L23.2173 4.72593L18.5057 10.5907V10.6480H22.0982L23.2758 4.72593L23.3023 4.68404L23.2758 4.66924Z"
                            />
                        </svg>

                        {/* Progress Steps */}
                        <nav className="flex items-center gap-6">
                            {steps.map((step) => (
                                <div
                                    key={step.number}
                                    className="flex items-center gap-1"
                                >
                                    <span
                                        className={
                                            step.number === currentStep
                                                ? "text-[15.5px] text-gray-800 underline underline-offset-[3px] decoration-1 border-b border-gray-400"
                                                : "text-[15.5px] text-gray-500"
                                        }
                                    >
                                        {step.number}. {step.label}
                                    </span>
                                </div>
                            ))}
                        </nav>
                    </div>

                    <button className="text-gray-800 text-[15.5px] underline decoration-1.5 decoration-blue-dark border-l border-[#0205bd] pl-4 -mr-12">
                        Login
                    </button>
                </div>
            </div>
        </header>
    );
};

export default ResultsHeader;
